import type { User } from '@lumi.new/sdk'
import { lumi } from '@/lib/lumi'
import { useEffect, useRef, useState } from 'react'

/**
 * Hook centralizado de autenticación.
 * - isLoading: true mientras el SDK no ha emitido el primer evento de sesión.
 * - Tolera fallos de red transitorios (Failed to fetch) sin romper la UI.
 */
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(lumi.auth.isAuthenticated)
  const [user, setUser] = useState<User | null>(lumi.auth.user)
  // Si el SDK ya tiene sesión sincrónica no necesitamos esperar
  const [isLoading, setIsLoading] = useState(!lumi.auth.isAuthenticated && lumi.auth.user === null)
  const resolvedRef = useRef(false)

  useEffect(() => {
    // Timeout de seguridad: si en 5 s no llega respuesta, asumimos no autenticado
    const timeout = setTimeout(() => {
      if (!resolvedRef.current) {
        resolvedRef.current = true
        setIsLoading(false)
      }
    }, 5000)

    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user }) => {
      setIsAuthenticated(isAuthenticated)
      setUser(user)
      if (!resolvedRef.current) {
        resolvedRef.current = true
        setIsLoading(false)
      }
    })

    return () => {
      clearTimeout(timeout)
      unsubscribe()
    }
  }, [])

  return {
    user,
    isAuthenticated,
    isLoading,
    isAdmin: user?.userRole === 'ADMIN',
  }
}
